<template>
  <AppModalWithConfirmation
    ref="appModalWithConfirmation"
    :confirm-close="areDataChanged"
    :show="show"
    :title="getModalTitle"
    class="lm-LevelModal"
    data-auto-testid="level-modal"
    data-testid="level-modal"
    hide-hero
    @on-close="onLevelModalClose"
  >
    <div class="lm-Body">
      <FormFieldNext :label="$t('levels.name')" data-auto-testid="manage-level-name" required>
        <AppInput
          ref="nameInput"
          v-model.trim="formModel[OKR_LEVEL_ENTITY_KEYS.NAME]"
          :is-error="isNameEmpty"
          :max-length="LEVEL_NAME_MAX_LENGTH"
          data-auto-testid="level-name-input"
          data-testid="level-name-input"
          size="xlg"
          style-type="primary"
          @focus="isNameEmpty = false"
          @update:model-value="validateName"
        />
        <AppFieldError
          v-if="isNameEmpty"
          :show="isNameEmpty"
          class="lm-FieldError"
          data-auto-testid="manage-level-name-is-error"
        >
          {{ $t('field.required') }}
        </AppFieldError>
      </FormFieldNext>

      <div data-auto-testid="manage-level-icon-create">
        <AppIconCreator class="lm-IconCreator">
          <template #first-term>
            <FormFieldNext :label="$t('levels.prefix')" class="lm-Prefix" required>
              <AppInput
                v-model.trim="formModel[OKR_LEVEL_ENTITY_KEYS.PREFIX]"
                :is-error="isPrefixEmpty"
                :max-length="LEVEL_PREFIX_MAX_LENGTH"
                data-auto-testid="level-prefix-input"
                data-testid="level-prefix-input"
                size="xlg"
                style-type="primary"
                @focus="isPrefixEmpty = false"
                @update:model-value="isPrefixEmpty = $event.length === 0"
              />
            </FormFieldNext>
          </template>

          <template #second-term>
            <AppPaletteColorPicker
              v-model:selected-color="formModel[OKR_LEVEL_ENTITY_KEYS.COLOR]"
              :palette="CATEGORIZED_COLORS"
            />
          </template>

          <template #result>
            <OkrIcon
              v-if="showIcon"
              :objective="iconObjective"
              data-auto-testid="result-icon"
              data-testid="result-icon"
            />
          </template>
        </AppIconCreator>
        <AppFieldError
          v-if="isPrefixEmpty"
          :show="isPrefixEmpty"
          class="lm-FieldError"
          data-auto-testid="manage-level-prefix-is-error"
        >
          <template v-if="isPrefixEmpty">
            {{ $t('field.required') }}
          </template>
        </AppFieldError>
      </div>

      <ElementTypeCategorySelect
        v-model="formModel[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]"
        :disabled="isEdit"
      >
        <template #description>
          {{ $t(selectedCategory.description) }}
        </template>
      </ElementTypeCategorySelect>

      <ElementTypeAllowParentControl
        v-if="isHasAllowWithoutParentField"
        v-model="formModel[OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT]"
      />

      <ElementTypeProgressOptionsSelect
        v-if="isHasProgressOptionsField"
        v-model="formModel[OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS]"
        :is-error="isProgressOptionsEmpty"
        :options="progressOptions"
        @remove-error="isProgressOptionsEmpty = false"
      />

      <GlobalGroupsWorkspaceSelect
        v-if="!isEdit"
        v-model:selected-workspaces="formModel.workspaceIds"
        data-auto-testid="manage-level-select-ws"
        hide-all-workspaces-option-in-selected-items-list
      />
    </div>

    <template #footer-actions>
      <AppButton
        data-auto-testid="cancel-button"
        data-testid="cancel-button"
        type="ghost-next"
        @click="close"
      >
        {{ $t('action.cancel') }}
      </AppButton>

      <AppButton
        :disabled="loading"
        :loading="loading"
        data-auto-testid="confirm-button"
        data-testid="confirm-button"
        type="primary-next"
        @click="save"
      >
        <template v-if="!isEdit">
          {{ $t('action.create') }}
        </template>
        <template v-else>
          {{ $t('action.update') }}
        </template>
      </AppButton>
    </template>
  </AppModalWithConfirmation>
</template>

<script>
import { cloneDeep, has, isEmpty, isNull } from 'lodash'
import { defineComponent } from 'vue'

import LevelsApiHandler from '@/api/level'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES, EVENT_NAMES, TRACKING_UNKNOWN } from '@/tracking/amplitude-helpers'
import { createUpdateLevelTrackingPayload } from '@/tracking/levels-tracking'
import { OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { isKR } from '@/utils/objective-types'
import { getProgressOptionByFieldTypeId } from '@/utils/okr-element-values'
import { DEFAULT_VALUE_FOR_FILTER } from '@/utils/okr-elements/filters'
import {
  CATEGORIZED_COLORS,
  ELEMENT_TYPES_CATEGORIES,
  findCategoryByTypeId,
  getLevelCategoryName,
  LEVEL_NAME_MAX_LENGTH,
  LEVEL_PREFIX_MAX_LENGTH,
  normalizeLevelColor,
  PROGRESS_OPTIONS_DEFAULT_VALUE
} from '@/utils/okr-levels'
import { objectOrNullProp } from '@/utils/prop-validators'
import { selectAllIsSelected } from '@/utils/select'

import AppModalWithConfirmation from '@/components/AppModalWithConfirmation'
import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import GlobalGroupsWorkspaceSelect from '@/components/global-groups/GlobalGroupsWorkspaceSelect'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIconCreator from '@/components/ui/AppIconCreator/AppIconCreator'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppPaletteColorPicker from '@/components/ui/AppPaletteColorPIcker/AppPaletteColorPicker'
import ElementTypeAllowParentControl from '@/components/workspaces/configuration-modal/element-types/ElementTypeAllowParentControl'
import ElementTypeCategorySelect from '@/components/workspaces/configuration-modal/element-types/ElementTypeCategorySelect'
import ElementTypeProgressOptionsSelect from '@/components/workspaces/configuration-modal/element-types/ElementTypeProgressOptionsSelect'

const levelsApi = new LevelsApiHandler()

const DEFAULT_FORM_MODEL = {
  [OKR_LEVEL_ENTITY_KEYS.NAME]: '',
  [OKR_LEVEL_ENTITY_KEYS.PREFIX]: '',
  [OKR_LEVEL_ENTITY_KEYS.COLOR]: CATEGORIZED_COLORS.VIOLET[1],
  [OKR_LEVEL_ENTITY_KEYS.TYPE_ID]: ELEMENT_TYPES_CATEGORIES.OBJECTIVE.typeId,
  [OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS]: PROGRESS_OPTIONS_DEFAULT_VALUE,
  [OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT]: false,
  workspaceIds: cloneDeep(DEFAULT_VALUE_FOR_FILTER),
  allWorkspaces: false
}

export default defineComponent({
  name: 'LevelModal',

  components: {
    ElementTypeProgressOptionsSelect,
    ElementTypeAllowParentControl,
    ElementTypeCategorySelect,
    GlobalGroupsWorkspaceSelect,
    AppIconCreator,
    AppPaletteColorPicker,
    FormFieldNext,
    AppModalWithConfirmation,
    AppButton,
    AppInput,
    AppFieldError,
    OkrIcon
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },

    modelValue: {
      default: null,
      validator: v => objectOrNullProp(v)
    },

    levelTemplate: {
      default: null,
      validator: v => objectOrNullProp(v)
    },

    trackingSource: {
      type: String,
      default: TRACKING_UNKNOWN
    }
  },

  emits: { 'update:show': null, create: null, edit: null },

  data() {
    return {
      formModel: cloneDeep(DEFAULT_FORM_MODEL),
      localFormModel: cloneDeep(DEFAULT_FORM_MODEL),
      isNameEmpty: false,
      isPrefixEmpty: false,
      isPrefixNotLetter: false,
      isProgressOptionsEmpty: false,
      loading: false
    }
  },

  computed: {
    LEVEL_NAME_MAX_LENGTH: () => LEVEL_NAME_MAX_LENGTH,

    LEVEL_PREFIX_MAX_LENGTH: () => LEVEL_PREFIX_MAX_LENGTH,

    OKR_LEVEL_ENTITY_KEYS: () => OKR_LEVEL_ENTITY_KEYS,

    CATEGORIZED_COLORS: () => CATEGORIZED_COLORS,

    selectedCategory() {
      return findCategoryByTypeId({
        typeId: this.formModel[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]
      })
    },

    progressOptions() {
      const { progressOptions = [] } = this.selectedCategory.additionalFields
      return progressOptions.map(item => {
        return {
          ...item,
          label: this.$t(item.label)
        }
      })
    },

    isHasProgressOptionsField() {
      return !isEmpty(this.progressOptions)
    },

    isHasAllowWithoutParentField() {
      return has(this.selectedCategory.additionalFields, OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT)
    },

    isEdit() {
      return !isNull(this.modelValue)
    },

    showIcon() {
      return this.formModel.prefix.trim().length > 0
    },

    getModalTitle() {
      return this.isEdit ? this.$t('levels.title.update') : this.$t('levels.title.create')
    },

    areDataChanged() {
      return JSON.stringify(this.localFormModel) !== JSON.stringify(this.formModel)
    },

    iconObjective() {
      return {
        levelColor: this.formModel.color.substring(1),
        levelPrefix: this.formModel.prefix
      }
    },

    isAllWorkspacesOptionSelected() {
      return selectAllIsSelected(this.formModel.workspaceIds)
    }
  },

  watch: {
    show(bool) {
      if (!bool) {
        this.onLevelModalClose()
        return
      }

      if (this.isEdit) {
        let { color } = this.modelValue

        this.formModel = {
          [OKR_LEVEL_ENTITY_KEYS.NAME]: this.modelValue[OKR_LEVEL_ENTITY_KEYS.NAME],
          [OKR_LEVEL_ENTITY_KEYS.PREFIX]: this.modelValue[OKR_LEVEL_ENTITY_KEYS.PREFIX],
          [OKR_LEVEL_ENTITY_KEYS.COLOR]: normalizeLevelColor({ color }),
          [OKR_LEVEL_ENTITY_KEYS.TYPE_ID]: this.modelValue[OKR_LEVEL_ENTITY_KEYS.TYPE_ID],
          [OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS]:
            this.modelValue[OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS] ||
            cloneDeep(DEFAULT_FORM_MODEL[OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS]),

          [OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT]:
            this.modelValue[OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT] ||
            DEFAULT_FORM_MODEL[OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT]
        }
      }

      if (!isNull(this.levelTemplate)) {
        this.formModel = {
          ...this.formModel,
          ...cloneDeep(this.levelTemplate)
        }
      }

      this.localFormModel = { ...this.formModel }

      // nextTick and setTimeout(both!) are needed to make input always focused on modal window
      // opening because modal window(o-modal) has transition of opacity with 0.2s
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.nameInput?.focus()
        }, 200)
      })
    }
  },

  methods: {
    onLevelModalClose() {
      this.$emit('update:show', false)

      // wait for modal close
      setTimeout(() => {
        this.formModel = cloneDeep(DEFAULT_FORM_MODEL)
        this.localFormModel = cloneDeep(DEFAULT_FORM_MODEL)
        this.isNameEmpty = false
        this.isPrefixEmpty = false
        this.isPrefixNotLetter = false
      }, 300)
    },

    validateName() {
      this.isNameEmpty = isStringEmpty(this.formModel.name)
    },

    async save() {
      this.validateName()
      this.isPrefixEmpty = isStringEmpty(this.formModel.prefix)
      this.isProgressOptionsEmpty =
        this.isHasProgressOptionsField &&
        isEmpty(this.formModel[OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS])

      if (this.isPrefixEmpty) {
        this.formModel.prefix = ''
      }
      if (this.isNameEmpty || this.isPrefixEmpty || this.isProgressOptionsEmpty) {
        return
      }

      let isSuccessful = true
      this.loading = true

      let level = null

      const requestMethod = this.isEdit
        ? payload => levelsApi.updateLevel(payload)
        : payload => levelsApi.createLevel(payload)

      try {
        const payload = {
          levelId: this.isEdit ? this.modelValue.id : undefined,
          ...this.formModel,
          color: this.formModel.color.substring(1), // remove '#' at the beginning
          orderValue: this.isEdit ? this.modelValue.orderValue : -1
        }

        if (
          !isKR({ [OKR_LEVEL_ENTITY_KEYS.TYPE_ID]: this.formModel[OKR_LEVEL_ENTITY_KEYS.TYPE_ID] })
        ) {
          payload[OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS] = null
        }

        if (!this.isEdit) {
          payload.workspaceIds = this.isAllWorkspacesOptionSelected
            ? []
            : this.formModel.workspaceIds
          payload.addToAllWorkspaces = this.isAllWorkspacesOptionSelected
        }

        const response = await requestMethod(payload)

        level = response.level

        if (!this.isEdit) {
          const trackingPayload = {
            category: EVENT_CATEGORIES.LEVEL_MANAGEMENT,
            label: this.formModel.name,
            source: this.trackingSource,
            type: this.$t(
              getLevelCategoryName({
                typeId: this.formModel[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]
              }),
              1,
              { locale: 'en' }
            ).toLowerCase()
          }

          if (
            isKR({ [OKR_LEVEL_ENTITY_KEYS.TYPE_ID]: this.formModel[OKR_LEVEL_ENTITY_KEYS.TYPE_ID] })
          ) {
            trackingPayload.parent_required = this.formModel[
              OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT
            ]
              ? 'no'
              : 'yes'

            trackingPayload.progress_options = this.formModel[
              OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS
            ].map(fieldTypeId => {
              const { label } = getProgressOptionByFieldTypeId(fieldTypeId)

              return this.$t(label, 1, { locale: 'en' }).toLowerCase()
            })
          }

          tracker.logEvent('Level created', trackingPayload)
        } else {
          tracker.logEvent(
            EVENT_NAMES.LEVEL_UPDATED,
            createUpdateLevelTrackingPayload({
              snapshot: this.modelValue,
              payload: this.formModel,
              typeId: this.modelValue[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]
            })
          )
        }
      } catch (error) {
        isSuccessful = false
        handleError({ error })
      }

      this.loading = false
      if (isSuccessful && !this.isEdit) {
        this.$emit('create', level)
      }
      if (isSuccessful && this.isEdit) {
        this.$emit('edit', level)
      }
    },

    close() {
      this.$refs.appModalWithConfirmation.close()
    }
  }
})
</script>

<style lang="scss" scoped>
.lm-Body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  --info-message-text-padding-top: 0;
}

.lm-Prefix {
  width: 80px;
}

// .lm-Switch {
//   margin-top: 8px;
// }
</style>

<style lang="scss">
.lm-LevelModal .o-modal-footer {
  padding-top: 8px;
}
</style>
